import { Injectable } from '@angular/core';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { PersonPaginationResponse, PersonDtoSelected, PersonDto } from '../../contracts/models/person_dto.model';
import { PeoplePaginationRequest } from 'app/pages/people/interfaces/pagination-request.interface';
import { Api } from '../../contracts/classes/api.class';
import { ApiResult } from '../../contracts/types/api-response.type';
import { PersonEmail } from '../../contracts/models/email-collection.model';
import { Person } from '../../contracts/models/person.model';
import { ProgramaFiltro, UbicacionFiltro } from 'app/pages/people/interfaces/search-form.interface';

@Injectable({ providedIn: 'root' })
export class PeopleService extends Api {

    private listPeople = new BehaviorSubject<PersonDto[] | PersonDtoSelected[]>([]);
    listPeople$ = this.listPeople.asObservable();

    addPeople(people: PersonDto[] | PersonDtoSelected[]) {
        this.listPeople.next(people);
    }

    getAll() {
        return this.http.get(`${this.url}/personas`);
    }

    public getListPagination(request): Observable<PersonPaginationResponse> {
        return this.http.get<PersonPaginationResponse>(`${this.url}/personas${request}`);
    }

    /**
     * Parámetro para los selectores de personas de los modales: las creadas más recientemente primero.
     * Sin él, la API devuelve el orden de siempre (las páginas no cambian).
     */
    private ordenParams(recientes: boolean): HttpParams {
        return recientes ? new HttpParams().set('orden', 'recientes') : new HttpParams();
    }

    getAllSelect(recientes = false) {
        return this.http.get(`${this.url}/personasData`, { params: this.ordenParams(recientes) });
    }

    getPersonasSelect(recientes = false) {
        return this.http.get(`${this.urlv2}/personas/select/persona-list`, { params: this.ordenParams(recientes) });
    }

    /** Países y regiones que tienen algún medio, para el filtro «País / Región» de la lista */
    getUbicaciones(): Observable<UbicacionFiltro[]> {
        return this.http.get<UbicacionFiltro[]>(`${this.urlv2}/personas/select/ubicaciones`);
    }

    /** Programas con su medio, para el filtro «Programa» de la lista */
    getProgramasSelect(): Observable<ProgramaFiltro[]> {
        return this.http.get<ProgramaFiltro[]>(`${this.urlv2}/personas/select/programas`);
    }

    searchPersonasSelect(search) {
        return this.http.get(`${this.urlv2}/personas/select/persona-list?search=${search}`);
    }

    getVoceros() {
        return this.http.get(`${this.url}/personas/data/getVoceros`);
    }

    getContactos(recientes = false) {
        return this.http.get(`${this.url}/personas/data/getContactos`, { params: this.ordenParams(recientes) });
    }

    /** soloActivos: oculta a los contactos desactivados de todos sus medios */
    getContactosSelect(recientes = false, soloActivos = false) {
        return this.http.get(`${this.urlv2}/personas/select/contact-list`, {
            params: this.soloActivosParams(this.ordenParams(recientes), soloActivos),
        });
    }

    searchContactosSelect(search, recientes = false, soloActivos = false) {
        return this.http.get(`${this.urlv2}/personas/select/contact-list`, {
            params: this.soloActivosParams(this.ordenParams(recientes), soloActivos).set('search', search),
        });
    }

    private soloActivosParams(params: HttpParams, soloActivos: boolean): HttpParams {
        return soloActivos ? params.set('solo_activos', '1') : params;
    }

    getVocerosSelect() {
        return this.http.get(`${this.urlv2}/personas/select/vocero-list`);
    }

    searchVocerosSelect(search) {
        return this.http.get(`${this.urlv2}/personas/select/vocero-list?search=${search}`);
    }

    getId(id: number) {
        return this.http.get(`${this.url}/personas/${id}`).pipe(
            map((response: any) => {
                return response.persona;
            })
        );
    }

    create(data: Person) {
        return this.http.post(`${this.url}/personas`, data);
    }

    edit(data: Person) {
        return this.http.put(`${this.url}/personas/${data.id}`, data);
    }

    delete(id: number) : Observable<string> {
        return this.http.delete<ApiResult<unknown>>(`${this.url}/personas/${id}`).pipe(map(result => result.message));
    }

    /** Reactiva una persona eliminada (junto con los datos de contacto eliminados con ella) */
    restore(id: number) : Observable<string> {
        return this.http.put<ApiResult<unknown>>(`${this.url}/personas/${id}/restore`, {}).pipe(map(result => result.message));
    }

    getAllEmails() {
        return this.http.get(`${this.url}/personaEmails`)
    }

    getEmailsByContact(id) {
        return this.http.get(`${this.url}/personaEmails/data/getListByPersona/${id}`)
    }

    deleteEmail(id: number) {
        return this.http.delete(`${this.url}/personaEmails/${id}`);
    }

    /** Refactor */

    public get_paginate_list(request: PeoplePaginationRequest = {
        search: '',
        nombres: '', apellidos: '', contacto: '', medio: null,
        pais: null, region: null,
        page: 1,
        type: [],
        category: [],
        cargo: [],
    }): Observable<PersonPaginationResponse> {
        let params = new HttpParams().set('page', request.page);
        if (request.size)                  params = params.append('size',      request.size);

        if (request.type?.length > 0)     params = params.append('type',     (request.type as number[]).join(','));
        if (request.category?.length > 0)  params = params.append('category',  request.category.join(','));
        if (request.cargo?.length > 0)     params = params.append('cargo',     request.cargo.join(','));
        if (request.programa != null)      params = params.append('programa_id', request.programa);
        if (request.tier?.length > 0)      params = params.append('tier',      request.tier.join(','));
        if (request.search?.length > 0)    params = params.append('search',    request.search);
        if (request.nombres?.length > 0)   params = params.append('nombres',   request.nombres);
        if (request.apellidos?.length > 0) params = params.append('apellidos', request.apellidos);
        if (request.contacto?.length > 0)  params = params.append('contacto',  request.contacto);
        if (request.medio != null)         params = params.append('medio_id', request.medio);
        if (request.pais != null)          params = params.append('pais',      request.pais);
        if (request.region != null)        params = params.append('region',    request.region);
        if (request.sort_by)               params = params.append('sort_by',   request.sort_by);
        if (request.sort_dir)              params = params.append('sort_dir',  request.sort_dir);
        // Sin estado la API trae solo las activas
        if (request.estado && request.estado !== 'activo') params = params.append('estado', request.estado);

        return this.http.get<PersonPaginationResponse>(
            `${this.urlv2}/personas`,
            { params: params, headers: this.getTenantHeaders() }
        );
    }

    public getEmailList(): Observable<PersonEmail[]> {
        return this.http.get<ApiResult<PersonEmail[]>>(`${this.urlv2}/emails/by-person`)
            .pipe(map(r => r.data))
    }

    searchPersons(search: string) {
        return this.http.get(`${this.url}/personasData?search=${search}`).pipe(
            map((response: any) => {
                return response.contactos;
            })
        );
    }

    public verify(id: number): Observable<any> {
        return this.http.get<ApiResult<unknown>>(`${this.url}/personas/verify/${id}`);
    }


    exportExcel(data): Observable<Blob> {
        return this.http.post(`${this.url}/personas/data/export`, data, {
          responseType: 'blob',
          withCredentials: true,
          headers: new HttpHeaders({
            'Accept': 'application/json'
          })
        });
    }
}
